import { useEffect, useState, useCallback } from 'react'
import { RefreshCw, Wifi, WifiOff, Clock, AlertTriangle, Users, CheckCircle } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

interface PresenceEntry {
  id: string
  firstName: string
  lastName: string
  establishmentId: string
  lastEvent: string | null
  lastEventAt: string | null
  lateMinutes: number | null
  adjustedTime: string | null
  isPresent: boolean
  plannedStart: string | null
  plannedEnd: string | null
}

interface ClockEventEntry {
  id: string
  eventType: string
  recordedAt: string
  lateMinutes: number | null
  adjustedTime: string | null
  deviceType: string
  employee: { id: string; firstName: string; lastName: string }
}

const EVENT_CONFIG: Record<string, { label: string; dot: string; badge: string; row: string }> = {
  arrive:  { label: 'Arrivée',      dot: 'bg-green-500',  badge: 'bg-green-100 text-green-700',   row: '' },
  resume:  { label: 'Reprise',      dot: 'bg-green-400',  badge: 'bg-green-100 text-green-700',   row: '' },
  break:   { label: 'Pause',        dot: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-700', row: '' },
  leave:   { label: 'Départ',       dot: 'bg-gray-400',   badge: 'bg-gray-100 text-gray-500',     row: '' },
}

const STATUS_ORDER: Record<string, number> = { arrive: 0, resume: 0, break: 1, leave: 2 }

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function fmtTimeFull(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function initials(first: string, last: string) {
  return `${first[0] || ''}${last !== '-' ? (last[0] || '') : ''}`.toUpperCase()
}

export default function ClockLive() {
  const { current: establishment } = useEstablishment()
  const [presence, setPresence] = useState<PresenceEntry[]>([])
  const [events, setEvents] = useState<ClockEventEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [now, setNow] = useState(new Date())
  const [view, setView] = useState<'presence' | 'timeline'>('presence')

  // Tick every second for clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const load = useCallback(async () => {
    try {
      const estabParam = establishment ? `?establishmentId=${establishment.id}` : ''
      const [presRes, evRes] = await Promise.all([
        api.get(`/clock/live${estabParam}`),
        api.get(`/clock/today${estabParam}`)
      ])
      setPresence(presRes.data)
      setEvents(evRes.data)
      setLastRefresh(new Date())
    } catch { /* ignore */ }
    setLoading(false)
  }, [establishment])

  useEffect(() => {
    load()
    const interval = setInterval(load, 30_000)
    return () => clearInterval(interval)
  }, [load])

  // Stats
  const presentCount = presence.filter(e => e.isPresent).length
  const breakCount = presence.filter(e => e.lastEvent === 'break').length
  const leftCount = presence.filter(e => e.lastEvent === 'leave').length
  const absentCount = presence.filter(e => !e.lastEvent && e.plannedStart).length
  const lateCount = presence.filter(e => e.lateMinutes && e.lateMinutes > 0 && e.lastEvent === 'arrive').length

  const sorted = [...presence].sort((a, b) => {
    const ao = STATUS_ORDER[a.lastEvent || ''] ?? 3
    const bo = STATUS_ORDER[b.lastEvent || ''] ?? 3
    if (ao !== bo) return ao - bo
    const aPlanned = a.plannedStart ? 1 : 0
    const bPlanned = b.plannedStart ? 1 : 0
    if (aPlanned !== bPlanned) return bPlanned - aPlanned
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
  })

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Badgeuse live</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Présences en temps réel — {now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            Mis à jour {fmtTimeFull(lastRefresh.toISOString())}
          </span>
          <button
            onClick={load}
            className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>
      </div>

      {/* Live clock */}
      <div className="bg-gray-900 rounded-xl px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-white font-mono text-2xl font-bold">
            {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        </div>
        <div className="flex gap-6 text-center">
          <div>
            <div className="text-green-400 font-bold text-xl">{presentCount}</div>
            <div className="text-gray-400 text-xs">Présents</div>
          </div>
          <div>
            <div className="text-yellow-400 font-bold text-xl">{breakCount}</div>
            <div className="text-gray-400 text-xs">Pause</div>
          </div>
          <div>
            <div className="text-gray-400 font-bold text-xl">{leftCount}</div>
            <div className="text-gray-400 text-xs">Partis</div>
          </div>
          {absentCount > 0 && (
            <div>
              <div className="text-red-400 font-bold text-xl">{absentCount}</div>
              <div className="text-gray-400 text-xs">Absents</div>
            </div>
          )}
          {lateCount > 0 && (
            <div>
              <div className="text-amber-400 font-bold text-xl">{lateCount}</div>
              <div className="text-gray-400 text-xs">Retards</div>
            </div>
          )}
        </div>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setView('presence')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'presence' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Users size={14} className="inline mr-1.5" />Présences
        </button>
        <button
          onClick={() => setView('timeline')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'timeline' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Clock size={14} className="inline mr-1.5" />Timeline du jour
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Chargement...</div>
      ) : view === 'presence' ? (
        // ── Presence grid ──────────────────────────────────────────
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {sorted.map(entry => {
            const cfg = entry.lastEvent ? EVENT_CONFIG[entry.lastEvent] : null
            const isLate = !!(entry.lateMinutes && entry.lateMinutes > 0 && entry.lastEvent === 'arrive')
            const noShow = !entry.lastEvent && !!entry.plannedStart

            return (
              <div
                key={entry.id}
                className={`bg-white rounded-xl border p-4 flex items-center gap-3 ${
                  isLate ? 'border-amber-300 bg-amber-50/30' :
                  noShow ? 'border-red-200 bg-red-50/30' :
                  'border-gray-100'
                }`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                    {initials(entry.firstName, entry.lastName)}
                  </div>
                  {cfg && (
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${cfg.dot}`} />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800 text-sm truncate">
                    {entry.firstName} {entry.lastName !== '-' ? entry.lastName : ''}
                  </div>
                  {entry.plannedStart ? (
                    <div className="text-xs text-gray-500">
                      Shift {entry.plannedStart} – {entry.plannedEnd}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">Pas de shift</div>
                  )}
                  {entry.lastEventAt && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {cfg?.label} à {fmtTime(entry.lastEventAt)}
                      {entry.adjustedTime && (
                        <span className="text-blue-500 ml-1">→ {entry.adjustedTime}</span>
                      )}
                    </div>
                  )}
                  {isLate && (
                    <div className="text-xs text-amber-600 font-medium flex items-center gap-1 mt-0.5">
                      <AlertTriangle size={11} /> Retard {entry.lateMinutes}min
                    </div>
                  )}
                  {noShow && (
                    <div className="text-xs text-red-500 font-medium mt-0.5">
                      Non pointé — prévu {entry.plannedStart}
                    </div>
                  )}
                </div>

                {/* Status badge */}
                <div className="flex-shrink-0">
                  {cfg ? (
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.badge}`}>
                      {cfg.label}
                    </span>
                  ) : entry.plannedStart ? (
                    <span className="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-600">Absent</span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-400">Repos</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        // ── Timeline ───────────────────────────────────────────────
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-700 text-sm">Pointages du jour — {events.length} événement(s)</h3>
          </div>
          {events.length === 0 ? (
            <div className="p-8 text-center text-gray-400">Aucun pointage enregistré aujourd'hui</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {events.map(ev => {
                const cfg = EVENT_CONFIG[ev.eventType]
                const isLate = !!(ev.lateMinutes && ev.lateMinutes > 0 && ev.eventType === 'arrive')
                return (
                  <div key={ev.id} className={`flex items-center gap-4 px-5 py-3 ${isLate ? 'bg-amber-50/40' : ''}`}>
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg?.dot || 'bg-gray-300'}`} />
                    <div className="font-mono text-sm text-gray-600 w-16 flex-shrink-0">
                      {fmtTime(ev.recordedAt)}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-gray-800 text-sm">
                        {ev.employee.firstName} {ev.employee.lastName !== '-' ? ev.employee.lastName : ''}
                      </span>
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded font-medium ${cfg?.badge || 'bg-gray-100 text-gray-500'}`}>
                        {cfg?.label || ev.eventType}
                      </span>
                      {ev.adjustedTime && (
                        <span className="ml-2 text-xs text-blue-500">→ {ev.adjustedTime}</span>
                      )}
                    </div>
                    {isLate && (
                      <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                        <AlertTriangle size={11} /> +{ev.lateMinutes}min
                      </span>
                    )}
                    <span className="text-xs text-gray-300 flex-shrink-0">
                      {ev.deviceType === 'mobile' ? '📱' : ev.deviceType === 'tablet' ? '📟' : '🖥'}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
